import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../../api/axios";
import { useNavigate } from "react-router-dom";
import SafeImage from "../../components/common/SafeImage";

const API_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : "http://localhost:8000/api";

export default function DriverCommission() {
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    fetchDrivers();
  }, []);

  async function fetchDrivers() {
    try {
      setLoading(true);
      setError("");

      // backend route
      const res = await apiClient.get(`/admin/driverscommissionsummary`, {});

      // Backend returns: { status:true, data:[...] }
      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setDrivers(list);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Error";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return drivers;
    return drivers.filter(
      (d) =>
        (d?.name || "").toLowerCase().includes(q) ||
        String(d?.driver_id || "").includes(q),
    );
  }, [drivers, search]);

  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>Driver Commission</h2>
          <p style={{ margin: "6px 0 0", opacity: 0.7 }}>
            View each driver’s total commission balance
          </p>
        </div>

        <button
          onClick={fetchDrivers}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #e5e7eb",
            background: "white",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          Refresh
        </button>
      </div>

      {/* Search Bar */}
      <div style={{ marginTop: 16 }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search driver by name / id..."
          style={{
            width: "100%",
            padding: "12px 14px",
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            outline: "none",
            fontSize: 14,
          }}
        />
      </div>

      {/* States */}
      {loading && (
        <div style={{ marginTop: 18, opacity: 0.7 }}>Loading drivers...</div>
      )}

      {!loading && error && (
        <div style={{ marginTop: 18, color: "#b91c1c" }}>{error}</div>
      )}

      {/* Cards Grid */}
      {!loading && !error && (
        <div
          style={{
            marginTop: 18,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 14,
          }}
        >
          {filtered.map((d) => (
            <DriverCard
              key={d.driver_id}
              driver={d}
              onView={() =>
                navigate(`/admin/driver-commission-requests/${d.driver_id}`)
              }
            />
          ))}

          {filtered.length === 0 && (
            <div style={{ opacity: 0.7 }}>No drivers found.</div>
          )}
        </div>
      )}
    </div>
  );
}

function DriverCard({ driver, onView }) {
  const name = driver?.name || "Driver";
  const image = driver?.profile_image ;
  const total = Number(driver?.commission_balance_total ?? 0);
  const driverId = driver?.driver_id || "N/A";
const currency = (driver?.currency_symbol || "").trim();

  // Format currency with commas
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div
      style={{
        border: "1px solid #E0E0E0",
        borderRadius: "8px",
        background: "#FFFFFF",
        padding: "16px",
        display: "flex",
        gap: "16px",
        alignItems: "center",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        transition: "all 0.2s ease",
        cursor: "pointer",
        height: "140px",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
        e.currentTarget.style.borderColor = "#0BAF9A";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
        e.currentTarget.style.borderColor = "#E0E0E0";
      }}
      onClick={() => {
        if (onView) onView(driver);
      }}
    >
      {/* Profile Image - Left Section */}
      <div style={{ position: "relative", flexShrink: 0 }}>
        <SafeImage
          src={image}
          alt={name}
          onError={(e) =>
            (e.currentTarget.src = "https://via.placeholder.com/80")
          }
          style={{
            width: "72px",
            height: "72px",
            borderRadius: "6px",
            objectFit: "cover",
            border: "2px solid #F5F5F5",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-4px",
            right: "-4px",
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            background: "#0BAF9A",
            border: "2px solid #FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="3.75" />
            <path d="M9 17.25v1.007a3 3 0 01-.879 2.122l-1.768 1.768a.75.75 0 001.28.53l1.414-1.414" />
            <path d="M18.244 17.25H21a.75.75 0 00.53-1.28l-1.768-1.768A3 3 0 0119.007 14" />
          </svg>
        </div>
      </div>

      {/* Content - Right Section */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {/* Top Section: Name and ID */}
        <div>
          <h3
            style={{
              margin: 0,
              fontWeight: 600,
              fontSize: "16px",
              color: "#333333",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: "1.3",
            }}
          >
            {name}
          </h3>
          <div
            style={{
              fontSize: "12px",
              color: "#666666",
              fontWeight: 400,
              marginTop: "2px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span>ID: {driverId}</span>
            <span
              style={{
                width: "4px",
                height: "4px",
                borderRadius: "50%",
                background: "#CCCCCC",
              }}
            />
            <span
              style={{
                color: "#0BAF9A",
                fontWeight: 500,
              }}
            >
              ● Active
            </span>
          </div>
        </div>

        {/* Middle Section: Commission */}
        <div>
          <div
            style={{
              fontSize: "11px",
              color: "#888888",
              fontWeight: 500,
              marginBottom: "4px",
            }}
          >
            TOTAL COMMISSION
          </div>
          <div
            style={{
              fontSize: "18px",
              fontWeight: 700,
              color: "#0BAF9A",
              letterSpacing: "-0.3px",
            }}
          >
          {total > 0 && currency
            ? `${currency} ${formatCurrency(total)}`
            : formatCurrency(total)}          </div>
        </div>
      </div>
    </div>
  );
}
